/**
 * Sanitiza HTML pegado desde Microsoft Word, Outlook, otras fuentes externas
 * o desde páginas web con Tailwind CSS u otros frameworks.
 * Usa DOMParser para un parseo real del árbol DOM, eliminando atributos,
 * tags y comentarios específicos de forma robusta.
 * Preserva el contenido textual y estructura básica limpia.
 */
export function sanitizeWordHtml(rawHtml: string): string {
  if (!rawHtml || !rawHtml.trim()) return rawHtml;

  // Eliminar comentarios HTML de Outlook/Word ANTES de parsear
  let cleaned = rawHtml.replace(/\u003c!--\s*StartFragment\s*--\u003e/gi, "");
  cleaned = cleaned.replace(/\u003c!--\s*EndFragment\s*--\u003e/gi, "");
  cleaned = cleaned.replace(/\u003c!--\[if\s+.*?\]\u003e.*?\u003c!\[endif\]--\u003e/gis, "");
  cleaned = cleaned.replace(/\u003c!--.*?--\u003e/gs, "");

  // Fast path: if no HTML tags at all, just normalize excessive newlines
  if (!/<[a-zA-Z][^>]*>/.test(cleaned)) {
    return cleaned.replace(/\n{3,}/g, "\n\n").trim();
  }

  // Wrap in a div so DOMParser handles fragments correctly
  const wrapped = `<div>${cleaned}</div>`;

  const parser = new DOMParser();
  const doc = parser.parseFromString(wrapped, "text/html");
  const container = doc.body.firstChild as HTMLDivElement | null;

  if (!container) return rawHtml;

  // Word/Tailwind namespace tag names to remove entirely (but keep their children)
  const wordTagPatterns = [/^o:/, /^v:/, /^w:/, /^m:/];

  // Attributes to strip entirely (name + value patterns)
  const attrRemovePatterns = [
    // class containing Mso
    { name: /^class$/i, value: /\bMso\w+/i },
    // style with Word-specific CSS
    {
      name: /^style$/i,
      value:
        /mso-|text-align:justify|tab-stops|font-family:\s*["']?Calibri|font-size:\s*\d+\.0pt|margin-|padding-|line-height:|background:/i,
    },
    // style with Tailwind CSS variables or other framework garbage
    {
      name: /^style$/i,
      value: /--tw-|-webkit-tap-highlight-color|text-size-adjust/i,
    },
    // any lang attribute
    { name: /^lang$/i, value: /.*/ },
  ];

  function cleanElement(el: Element): void {
    const tagName = el.tagName.toLowerCase();

    // If it's a Word namespace tag, unwrap it (keep children, remove tag)
    if (wordTagPatterns.some((p) => p.test(tagName))) {
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
      }
      return;
    }

    // Remove unwanted attributes
    const attrsToRemove: string[] = [];
    for (let i = 0; i < el.attributes.length; i++) {
      const attr = el.attributes[i];
      for (const pattern of attrRemovePatterns) {
        if (pattern.name.test(attr.name) && pattern.value.test(attr.value)) {
          attrsToRemove.push(attr.name);
          break;
        }
      }
    }
    attrsToRemove.forEach((name) => el.removeAttribute(name));

    // Remove empty class/style attributes
    if (el.getAttribute("class") === "") el.removeAttribute("class");
    if (el.getAttribute("style") === "") el.removeAttribute("style");

    // Recurse into children
    Array.from(el.children).forEach((child) => cleanElement(child));
  }

  Array.from(container.children).forEach((child) => cleanElement(child));

  let result = container.innerHTML;

  // Remove <br> tags that only have style attributes (after style cleanup)
  result = result.replace(/<br[^>]*\bstyle\b[^>]*>/gi, "<br>");

  // Post-process: remove truly empty block/inline tags (including those with only <br>)
  // Repeated passes until stable
  let prevResult = "";
  while (prevResult !== result) {
    prevResult = result;
    result = result.replace(/<(p|div|span|h[1-6]|blockquote)[^>]*>(\s|<br\s*\/?>)*<\/\1>/gi, "");
  }

  // Convert any remaining <br> or <br /> to double newlines for clean paragraph splitting
  result = result.replace(/<br\s*\/?>/gi, "\n\n");

  // Convert any remaining HTML paragraph structure to plain text paragraphs
  // Replace </p><p> boundaries with double newlines
  result = result.replace(/<\/p>\s*<p[^>]*>/gi, "\n\n");
  // Remove remaining <p> and </p> tags
  result = result.replace(/<\/?p[^>]*>/gi, "");

  // Strip any other remaining HTML tags (lists, blockquotes, divs, spans, etc.)
  result = result.replace(/<[^>]+>/g, "");

  // Normalize &nbsp; and horizontal whitespace (spaces/tabs), but PRESERVE line breaks
  result = result.replace(/&nbsp;/gi, " ");
  // Collapse multiple spaces/tabs, but keep line breaks intact
  result = result.replace(/[ \t]{2,}/g, " ");
  result = result.replace(/>\s+</g, "><");

  // Clean up excessive newlines (3+ -> double newline)
  result = result.replace(/\n{3,}/g, "\n\n");
  // Trim whitespace around newlines
  result = result.replace(/\n\s+\n/g, "\n\n");

  return result.trim();
}

/**
 * Sanitiza HTML de entrevistas pegado desde fuentes externas.
 * A diferencia de sanitizeWordHtml, esta función PRESERVA las tags HTML
 * estructurales útiles (p, strong, em, h2, h3, blockquote, ul, ol, li, a, br)
 * mientras limpia la basura de Word (estilos inline, clases Mso, namespaces).
 *
 * Ideal para entrevistas donde se necesita formato: preguntas en negrita,
 * respuestas en párrafos, citas memorables en blockquote, etc.
 */
export function sanitizeInterviewHtml(rawHtml: string): string {
  if (!rawHtml || !rawHtml.trim()) return rawHtml;

  // Quick check: if it's already clean-looking HTML from our editor, return as-is.
  // IMPORTANTE: requiere que exista al menos una etiqueta HTML. De lo contrario,
  // el texto plano heredado (párrafos separados por \n\n) caería aquí y se
  // devolvería sin envolver en <p>, colapsando los saltos de línea al cargarlo
  // en el editor. Sin una etiqueta, dejamos que continúe al fast-path de abajo,
  // que sí envuelve cada párrafo en <p>.
  const hasHtmlTag = /<[a-zA-Z][^>]*>/.test(rawHtml);
  const isEditorHtml = hasHtmlTag &&
    !rawHtml.includes('contenteditable') &&
    !rawHtml.includes('class="Mso') &&
    !rawHtml.includes('mso-');
  if (isEditorHtml && !rawHtml.includes('o:') && !rawHtml.includes('v:')) {
    // Still normalize whitespace
    return rawHtml.replace(/\n{3,}/g, "\n\n").trim();
  }

  // Eliminar comentarios HTML de Outlook/Word ANTES de parsear
  let cleaned = rawHtml.replace(/<!--\s*StartFragment\s*-->/gi, "");
  cleaned = cleaned.replace(/<!--\s*EndFragment\s*-->/gi, "");
  cleaned = cleaned.replace(/<!--\[if\s+.*?\]-->.*?<!\[endif\]-->/gis, "");
  cleaned = cleaned.replace(/<!--.*?-->/gs, "");

  // Fast path: if no HTML tags at all, wrap in simple paragraph
  if (!/<[a-zA-Z][^>]*>/.test(cleaned)) {
    const paragraphs = cleaned
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n\n")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (paragraphs.length === 0) return cleaned.trim();
    return paragraphs.map((p) => `<p>${escapeHtmlEntities(p)}</p>`).join("");
  }

  // Wrap in a div so DOMParser handles fragments correctly
  const wrapped = `<div>${cleaned}</div>`;

  const parser = new DOMParser();
  const doc = parser.parseFromString(wrapped, "text/html");
  const container = doc.body.firstChild as HTMLDivElement | null;

  if (!container) return rawHtml;

  // Tags that are allowed in interview content
  const allowedTags = new Set([
    "p", "br", "strong", "b", "em", "i", "u", "h2", "h3", "h4",
    "blockquote", "ul", "ol", "li", "a", "span", "div",
    // Imágenes en línea insertadas desde el editor enriquecido
    "img", "figure", "figcaption",
  ]);

  // Word/Tailwind namespace tag names to remove entirely (but keep their children)
  const wordTagPatterns = [/^o:/, /^v:/, /^w:/, /^m:/];

  // Attributes to strip from all elements (no exceptions)
  const attrStripPatterns = [
    // class containing Mso
    { name: /^class$/i, value: /\bMso\w+/i },
    // any style attribute (we strip ALL inline styles from pasted content)
    { name: /^style$/i, value: /.*/ },
    // any lang attribute
    { name: /^lang$/i, value: /.*/ },
    // xml namespaces
    { name: /^xmlns/i, value: /.*/ },
  ];

  function cleanInterviewElement(el: Element): void {
    const tagName = el.tagName.toLowerCase();

    // If it's a Word namespace tag, unwrap it (keep children, remove tag)
    if (wordTagPatterns.some((p) => p.test(tagName))) {
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
      }
      return;
    }

    // If tag is not in allowed list, unwrap it (keep children)
    if (!allowedTags.has(tagName)) {
      const parent = el.parentNode;
      if (parent && tagName !== "html" && tagName !== "body" && tagName !== "head") {
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
      }
      return;
    }

    // For allowed tags: remove unwanted attributes
    const attrsToRemove: string[] = [];
    for (let i = 0; i < el.attributes.length; i++) {
      const attr = el.attributes[i];
      for (const pattern of attrStripPatterns) {
        if (pattern.name.test(attr.name) && pattern.value.test(attr.value)) {
          attrsToRemove.push(attr.name);
          break;
        }
      }
    }
    attrsToRemove.forEach((name) => el.removeAttribute(name));

    // Special handling for <a>: keep href but sanitize it
    if (tagName === "a") {
      const href = el.getAttribute("href") || "";
      // Remove all attributes first
      while (el.attributes.length > 0) {
        el.removeAttribute(el.attributes[0].name);
      }
      // Only re-add safe href
      if (href && (href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:") || href.startsWith("tel:"))) {
        el.setAttribute("href", href);
      }
      // Add target and rel for external links
      if (href.startsWith("http")) {
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer nofollow");
      }
    }

    // Special handling for <img>: keep only a safe src + alt, drop everything else
    if (tagName === "img") {
      const src = el.getAttribute("src") || "";
      const alt = el.getAttribute("alt") || "";
      // Remove all attributes first
      while (el.attributes.length > 0) {
        el.removeAttribute(el.attributes[0].name);
      }
      const safeSrc =
        src.startsWith("http://") ||
        src.startsWith("https://") ||
        src.startsWith("data:image/");
      if (safeSrc) {
        el.setAttribute("src", src);
        if (alt) el.setAttribute("alt", alt);
        el.setAttribute("loading", "lazy");
      } else {
        // Imagen con src inseguro: eliminar el elemento por completo
        el.parentNode?.removeChild(el);
        return;
      }
    }

    // Remove empty class/style attributes
    if (el.getAttribute("class") === "") el.removeAttribute("class");
    if (el.getAttribute("style") === "") el.removeAttribute("style");

    // Recurse into children
    Array.from(el.children).forEach((child) => cleanInterviewElement(child));
  }

  Array.from(container.children).forEach((child) => cleanInterviewElement(child));

  let result = container.innerHTML;

  // Remove empty tags (those with only whitespace or <br>)
  let prevResult = "";
  while (prevResult !== result) {
    prevResult = result;
    result = result.replace(/<(p|div|span|h[1-6]|blockquote|li)[^>]*>(\s|<br\s*\/?>)*<\/\1>/gi, "");
  }

  // Clean up Word-specific empty paragraphs with non-breaking spaces
  result = result.replace(/<p[^>]*>&nbsp;<\/p>/gi, "");
  result = result.replace(/<p[^>]*>\s*<\/p>/gi, "");

  // Normalize multiple consecutive <br> to a single paragraph break
  result = result.replace(/(<br\s*\/?>\s*){2,}/gi, "</p><p>");

  // Ensure blockquote content is wrapped in paragraphs if bare text
  // (skip - let the browser handle it)

  // Normalize whitespace in HTML but preserve structure
  result = result.replace(/&nbsp;/gi, " ");
  result = result.replace(/>\s+</g, "><");
  result = result.replace(/\n\s*\n/g, "\n");

  // Final cleanup: remove truly empty divs that might remain
  result = result.replace(/<div[^>]*>\s*<\/div>/gi, "");

  // If the result doesn't start with a block tag, wrap it
  if (result.trim() && !/^<(p|h[1-6]|blockquote|ul|ol|div|figure)/i.test(result.trim())) {
    result = `<p>${result.trim()}</p>`;
  }

  return result.trim();
}

function escapeHtmlEntities(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Convierte HTML enriquecido (como el de Lexical o Word) a texto plano
 * limpio. Convierte <p> y <br> en saltos de línea, elimina todo otro HTML,
 * y normaliza espacios. Ideal para campos que se renderizan como texto
 * plano (extracto, lead, etc.).
 */
export function htmlToPlainText(html: string): string {
  if (!html || !html.trim()) return "";

  // Detectar editor vacío: <p><br></p>, <p></p>, o similares
  const emptyEditorRegex = /<p[^>]*>(\s|&nbsp;|(<br\s*\/?>))*<\/p>/gi;
  const stripped = html.replace(emptyEditorRegex, "").trim();
  if (stripped === "") return "";

  let text = html;

  // Replace paragraph boundaries with double newlines
  text = text.replace(/<\/p>\s*<p[^>]*>/gi, "\n\n");
  // Remove remaining <p> and </p> tags
  text = text.replace(/<\/?p[^>]*>/gi, "");

  // Replace <br> with single newline
  text = text.replace(/<br\s*\/?>/gi, "\n");

  // Remove all remaining HTML tags
  text = text.replace(/<[^>]+>/g, "");

  // Decode common HTML entities
  text = text.replace(/&nbsp;/gi, " ");
  text = text.replace(/&amp;/gi, "&");
  text = text.replace(/&lt;/gi, "<");
  text = text.replace(/&gt;/gi, ">");
  text = text.replace(/&quot;/gi, '"');
  text = text.replace(/&#39;/gi, "'");

  // Normalize whitespace: collapse 3+ newlines to double newline
  text = text.replace(/\n{3,}/g, "\n\n");
  // Collapse multiple spaces/tabs into one
  text = text.replace(/[ \t]{2,}/g, " ");
  // Trim whitespace around newlines
  text = text.replace(/\n\s+\n/g, "\n\n");
  text = text.trim();

  return text;
}